import React, { useEffect } from 'react';
import './AnonymousLanding.css';
import BeTheClientModalDemo from './BeTheClientModalDemo';
import ClientSimulationShowcase from './ClientSimulationShowcase';
import LandingDemoShowcase from './LandingDemoShowcase';
import FullSessionModalDemo from './FullSessionModalDemo';
import SpecificSkillModalDemo from './SpecificSkillModalDemo';
import { uoftLogoUrl, camhLogoUrl } from '../../assets/assetPaths';
import useMediaQuery from '../../hooks/useMediaQuery';

function AnonymousLanding({ onSignIn, onLearnMore, onAbout, onClients, onCounsellors, onTutorial, onCoding, overlayOpen = false }) {
  const [introVideoOpen, setIntroVideoOpen] = React.useState(false);
  const [selectedModeId, setSelectedModeId] = React.useState('specific-skill');
  const [showResearchBanner, setShowResearchBanner] = React.useState(true);
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);
  const isMobileLandingViewport = useMediaQuery('(max-width: 768px)');

  useEffect(() => {
    document.body.classList.add('anonymous-landing-page');
    document.documentElement.classList.add('anonymous-landing-page');

    return () => {
      document.body.classList.remove('anonymous-landing-page');
      document.documentElement.classList.remove('anonymous-landing-page');
    };
  }, []);

  const modeCards = [
    {
      id: 'specific-skill',
      title: 'Specific Skill',
      body: 'Practice one MI skill at a time in short, focused exercises.',
      modalDescription: [
        'Learners respond to brief counselling moments, such as simple or complex reflection prompts, and get immediate feedback on the MI quality of their answer.',
        'This mode is useful for deliberate practice before longer simulations. Educators can use it to isolate one behavior, repeat similar prompts, and build precision step by step.',
      ],
    },
    {
      id: 'full-session',
      title: 'Full Session',
      body: 'Run a complete counselling conversation from start to finish.',
      modalDescription: [
        'Learners choose a virtual client, conduct an extended MI session, and then review the transcript, scores, and feedback after the conversation ends.',
        'This mode supports assessment and debrief. It helps educators evaluate pacing, rapport, reflections, and responsiveness across a full encounter.',
      ],
    },
    {
      id: 'be-the-client',
      title: 'Be the Client',
      body: 'Take the client role and observe AIMIE model MI technique.',
      modalDescription: [
        'Learners answer as the client while AIMIE plays the counsellor, making it easier to study how reflections and questions shape motivation.',
        'This mode is useful for modeling and discussion. Students can experience MI from the receiving side and analyze strong technique turn by turn.',
      ],
    },
  ];

  const renderModeDemo = (modeId) => {
    if (modeId === 'specific-skill') {
      return <SpecificSkillModalDemo />;
    }
    if (modeId === 'full-session') {
      return <FullSessionModalDemo />;
    }
    return <BeTheClientModalDemo />;
  };

  const selectedMode = modeCards.find((mode) => mode.id === selectedModeId) || modeCards[0];
  const handleNavAction = (callback) => () => {
    setMobileNavOpen(false);
    callback?.();
  };
  const handleModeSelect = (modeId) => {
    setSelectedModeId(modeId);
  };

  return (
    <div className="anonymous-landing">
      {!overlayOpen && (
        <div className="anonymous-landing__topbar">
          <header className="anonymous-landing__header">
            <div className="anonymous-landing__brand">AIMIE</div>
            <nav className={`anonymous-landing__nav${mobileNavOpen ? ' anonymous-landing__nav--open' : ''}`} aria-label="Landing navigation">
              {onAbout && (
                <button
                  type="button"
                  className="anonymous-landing__nav-btn"
                  onClick={handleNavAction(onAbout)}
                >
                  About
                </button>
              )}
              {onClients && (
                <button
                  type="button"
                  className="anonymous-landing__nav-btn"
                  onClick={handleNavAction(onClients)}
                >
                  Clients
                </button>
              )}
              {onCounsellors && (
                <button
                  type="button"
                  className="anonymous-landing__nav-btn"
                  onClick={handleNavAction(onCounsellors)}
                >
                  Counsellors
                </button>
              )}
              {onTutorial && (
                <button
                  type="button"
                  className="anonymous-landing__nav-btn"
                  onClick={handleNavAction(onTutorial)}
                >
                  Tutorial
                </button>
              )}
              {onCoding && (
                <button
                  type="button"
                  className="anonymous-landing__nav-btn"
                  onClick={handleNavAction(onCoding)}
                >
                  Coding
                </button>
              )}
            </nav>
            <div className="anonymous-landing__header-actions">
              <button
                type="button"
                className="anonymous-landing__sign-in"
                onClick={onSignIn}
              >
                Sign in / sign up
              </button>
              <button
                type="button"
                className={`anonymous-landing__mobile-menu-btn${mobileNavOpen ? ' anonymous-landing__mobile-menu-btn--open' : ''}`}
                onClick={() => setMobileNavOpen((open) => !open)}
                aria-label={mobileNavOpen ? 'Close navigation menu' : 'Open navigation menu'}
                aria-expanded={mobileNavOpen}
              >
                <span />
                <span />
                <span />
              </button>
            </div>
          </header>
          {showResearchBanner && (
            <div
              className="anonymous-landing__research-banner"
              role="note"
              aria-label="Research project notice"
            >
              <span className="anonymous-landing__research-banner-text">
                AIMIE is a research project currently in active development. Some features may be incomplete.
              </span>
              <button
                type="button"
                className="anonymous-landing__research-banner-close"
                aria-label="Dismiss research project notice"
                onClick={() => setShowResearchBanner(false)}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          )}
        </div>
      )}

      <main className="anonymous-landing__main">
        <section className="anonymous-landing__hero">
          <div className="anonymous-landing__hero-inner">
            <h1 className="anonymous-landing__hero-title">Your ultimate guide to MI</h1>
            <button
              type="button"
              className="anonymous-landing__cta anonymous-landing__cta--primary"
              onClick={onSignIn}
            >
              Get Started
            </button>
          </div>
        </section>

        <section className="anonymous-landing__intro-video">
          <button
            type="button"
            className="anonymous-landing__intro-video-trigger"
            onClick={() => setIntroVideoOpen(true)}
            aria-label="Watch the AIMIE introduction video"
          >
              <img
                src="/decoded-assets/aimieIntroLectureWallpaperBright.webp"
                alt="AIMIE introduction video thumbnail"
                className="anonymous-landing__intro-video-thumb"
              />
            <span className="anonymous-landing__intro-video-play" aria-hidden="true">
              <svg viewBox="0 0 48 48">
                <circle cx="24" cy="24" r="22" fill="rgba(0,0,0,0.45)" />
                <path d="M19 14l18 10-18 10z" fill="#f7f4ec" />
              </svg>
            </span>
          </button>
        </section>

        <section className="anonymous-landing__showcase">
          <p className="anonymous-landing__eyebrow">Built from real conversations.</p>
          <h2 className="anonymous-landing__section-title">Client simulations shaped by real human motivational interviewing transcripts.</h2>
          <div className="anonymous-landing__showcase-card anonymous-landing__showcase-card--simulation">
            <div className="anonymous-landing__showcase-visual">
              <ClientSimulationShowcase />
            </div>
          </div>
        </section>

        <section className="anonymous-landing__showcase">
          <p className="anonymous-landing__eyebrow">Real feedback. Right now.</p>
          <h2 className="anonymous-landing__section-title">See every response scored, explained, and tracked in real time.</h2>
          <div className="anonymous-landing__showcase-card anonymous-landing__showcase-card--feedback">
            <div className="anonymous-landing__showcase-visual">
              <LandingDemoShowcase
                showPredictedCoding={!isMobileLandingViewport}
                showMetrics
                hideMotivationCorrelation={isMobileLandingViewport}
                className="anonymous-landing__demo--mobile-feedback"
              />
            </div>
          </div>
        </section>

        <section id="landing-modes" className="anonymous-landing__modes">
          <p className="anonymous-landing__eyebrow">3 Modes 1 goal</p>
          <h2 className="anonymous-landing__section-title">Train across the full arc of motivational interviewing</h2>
          <div className="anonymous-landing__mode-showcase-layout">
            <div className="anonymous-landing__mode-showcase-nav" aria-label="Training modes">
              {modeCards.map((mode) => (
                <button
                  key={mode.id}
                  type="button"
                  className={`anonymous-landing__mode-showcase-option${mode.id === selectedMode.id ? ' anonymous-landing__mode-showcase-option--active' : ''}`}
                  onPointerDown={() => handleModeSelect(mode.id)}
                  onClick={() => handleModeSelect(mode.id)}
                >
                  {mode.title}
                </button>
              ))}
            </div>
            <article className="anonymous-landing__mode-showcase-card">
              <div className="anonymous-landing__mode-showcase-copy">
                <h3>{selectedMode.title}</h3>
                <p className="anonymous-landing__mode-showcase-summary">{selectedMode.body}</p>
                {selectedMode.modalDescription.map((paragraph) => (
                  <p key={paragraph} className="anonymous-landing__mode-showcase-detail">{paragraph}</p>
                ))}
              </div>
              <div className="anonymous-landing__mode-showcase-visual">
                {renderModeDemo(selectedMode.id)}
              </div>
            </article>
          </div>
        </section>

        <section className="anonymous-landing__footer-cta">
          <h2 className="anonymous-landing__footer-title">Your first step to becoming an MI expert</h2>
          <button
            type="button"
            className="anonymous-landing__cta anonymous-landing__cta--secondary"
            onClick={onLearnMore}
          >
            Learn more
          </button>
        </section>

        <footer className="anonymous-landing__partners">
          <img src={uoftLogoUrl} alt="University of Toronto" className="anonymous-landing__partner-logo anonymous-landing__partner-logo--uoft" />
          <img src={camhLogoUrl} alt="CAMH" className="anonymous-landing__partner-logo anonymous-landing__partner-logo--camh" />
        </footer>
      </main>

      {introVideoOpen && (
        <div
          className="anonymous-landing__modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="intro-video-title"
          onClick={() => setIntroVideoOpen(false)}
        >
          <div
            className="anonymous-landing__intro-video-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="anonymous-landing__intro-video-modal-header">
              <h3 id="intro-video-title">Introduction to AIMIE</h3>
              <button
                type="button"
                className="anonymous-landing__modal-close"
                onClick={() => setIntroVideoOpen(false)}
                aria-label="Close video"
              >
                ×
              </button>
            </div>
            <div className="anonymous-landing__intro-video-modal-frame">
              <iframe
                src="https://www.youtube.com/embed/E9OqqOUafAo?autoplay=1"
                title="AIMIE introduction video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default AnonymousLanding;
